// Rail items + groups for the Rollover Rep app. Kept in one file so future
// route additions only touch this list, not the rail components or the shell.
//
// Each NavItem.id is the canonical route identifier used by `pickActiveId`
// below to mark a rail item active based on the current pathname. Picking
// the longest matching prefix means /Projects/Roadmap correctly highlights
// the "projects" item, /Operator/Teams highlights "team", etc.

import type { NavGroup, NavItem } from './types';

/** Operator rail — internal staff (isAdmin === true). */
export const OPERATOR_GROUPS: NavGroup[] = [
  {
    group: 'Workspace',
    items: [
      // Day-to-day surfaces. Projects houses the PM Suite (which has its own
      // sub-rail once you're inside it); Integrations is the configuration
      // surface for telephony / CRM hookups.
      {
        id: 'projects',
        label: 'Projects',
        to: '/Projects',
        iconPath: 'M3 7h18M3 12h18M3 17h18',
      },
      {
        id: 'integrations',
        label: 'Integrations',
        to: '/Integrations',
        iconPath: 'M12 2a10 10 0 1 0 10 10M2 12h20M12 2v20',
      },
    ],
  },
  {
    group: 'Operations',
    items: [
      // The Operator hub — Teams + Internal Team + Receiving Numbers all live
      // under /Operator/* in the existing routes. They surface here as
      // separate primary destinations because that's how operators think
      // about them ("I'm managing a customer team" vs "I'm reviewing internal
      // staff" vs "I'm provisioning a number").
      {
        id: 'customers',
        label: 'Customers',
        to: '/Operator/Teams',
        iconPath: 'M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM3 21a7 7 0 0 1 14 0',
      },
      {
        id: 'internal',
        label: 'Internal team',
        to: '/Operator/Internal',
        iconPath: 'M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z',
      },
      {
        id: 'numbers',
        label: 'Receiving numbers',
        to: '/Operator/Receiving-Numbers',
        iconPath: 'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7a2 2 0 0 1 1.72 2z',
      },
      // Inbox of user-submitted feedback. Operator-only; the customer-side
      // /Feedback page (in the Personal group below) is where they submit.
      {
        id: 'feedback-inbox',
        label: 'Feedback inbox',
        to: '/Operator/Feedback',
        iconPath:
          'M4 4h16v12H5.17L4 17.17V4zm0-2a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H4z',
      },
      // Call-recordings portal. The page self-gates on recordingsRole — an
      // operator without recordings access sees an access notice.
      {
        id: 'recordings',
        label: 'Recordings',
        to: '/Recordings',
        iconPath: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-2 6 6 4-6 4z',
      },
      // Intake-logs portal (CloudWatch dashboard for the intake lambdas).
      // Self-gates on logsRole — operators without a grant see a notice.
      {
        id: 'logs',
        label: 'Intake logs',
        to: '/Operator/Logs',
        iconPath: 'M4 3h16v18H4zM8 8h8M8 12h8M8 16h5',
      },
      // Unified permissions matrix. Self-gates to operator super_admin — other
      // operators see a notice.
      {
        id: 'permissions',
        label: 'Permissions',
        to: '/Operator/Permissions',
        iconPath: 'M12 2a5 5 0 0 0-5 5v3H5v12h14V10h-2V7a5 5 0 0 0-5-5zm-3 8V7a3 3 0 0 1 6 0v3z',
      },
      // Executive dashboard. Self-gates on exec_dashboard.
      {
        id: 'exec',
        label: 'Exec dashboard',
        to: '/Operator/Exec',
        iconPath: 'M3 3v18h18M7 14l4-4 3 3 5-6',
      },
      // Monthly calls-vs-forms billing reconciliation. Self-gates on
      // exec_dashboard + finance — operators without finance see a notice.
      {
        id: 'billing-audit',
        label: 'Billing audit',
        to: '/Operator/Billing-Audit',
        iconPath: 'M4 3h12l4 4v14H4zM8 12h8M8 16h8M8 8h5',
      },
      // Marketing-site CMS — the redirect/short-link table behind
      // rolloverrep.com. Self-gates on the `website` permission; operators
      // without a grant see an access notice.
      {
        id: 'website',
        label: 'Website',
        to: '/Operator/Website',
        iconPath:
          'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM2 12h20M12 2c2.6 2.7 4 6.3 4 10s-1.4 7.3-4 10c-2.6-2.7-4-6.3-4-10s1.4-7.3 4-10z',
      },
    ],
  },
  {
    // Personal lives at the bottom so it doesn't compete with the
    // workspace-level navigation. Settings is a one-stop for account
    // preferences (theme, notification opt-outs, integrations the user
    // owns personally) — page is a placeholder for now and grows as
    // those features come online.
    group: 'Personal',
    items: [
      {
        id: 'settings',
        label: 'Settings',
        to: '/Settings',
        iconPath:
          'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM3 12h2m14 0h2M12 3v2m0 14v2',
      },
      // Feedback is intentionally Personal, not Workspace — it's a "from me,
      // about the app" surface, not part of the day-to-day workflow. Lives
      // here on both rails so customers and operators alike can reach it.
      {
        id: 'feedback',
        label: 'Feedback',
        to: '/Feedback',
        iconPath:
          'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
      },
    ],
  },
];

/** Customer CRM rail — non-admin users. Slim, icon-only.
 *  Projects is intentionally NOT here: PM is operator-only per the
 *  security requirement (server-side RTDB rules enforce, the rail just
 *  doesn't surface the link). */
export const CRM_PRIMARY: NavItem[] = [
  {
    id: 'integrations',
    label: 'Integrations',
    to: '/Integrations',
    iconPath: 'M12 2a10 10 0 1 0 10 10M2 12h20M12 2v20',
  },
  {
    id: 'teams',
    label: 'Teams',
    to: '/Operator/Teams',
    iconPath: 'M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM3 21a7 7 0 0 1 14 0',
  },
  // Call-recordings portal — the primary surface for agents (who are non-
  // operator users). Self-gates on recordingsRole.
  {
    id: 'recordings',
    label: 'Recordings',
    to: '/Recordings',
    iconPath: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-2 6 6 4-6 4z',
  },
];

/** Recordings-only rail — for provisioned call agents (recordingsRole set,
 *  NOT operators). They get ONLY the Recordings section; Settings stays in the
 *  footer purely as the sign-out surface. Everything else (Integrations, CRM
 *  teams, PM) is intentionally absent — and the routes redirect away too. */
export const RECORDINGS_AGENT_PRIMARY: NavItem[] = [
  {
    id: 'recordings',
    label: 'Recordings',
    to: '/Recordings',
    iconPath: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-2 6 6 4-6 4z',
  },
];
export const RECORDINGS_AGENT_FOOTER: NavItem[] = [
  {
    id: 'settings',
    label: 'Settings',
    to: '/Settings',
    iconPath: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM3 12h2m14 0h2M12 3v2m0 14v2',
  },
];

export const CRM_FOOTER: NavItem[] = [
  // Settings + Feedback in the footer. Customers don't have a top-bar
  // user menu (yet), so this is where they find "send us a note."
  {
    id: 'settings',
    label: 'Settings',
    to: '/Settings',
    iconPath: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM3 12h2m14 0h2M12 3v2m0 14v2',
  },
  {
    id: 'feedback',
    label: 'Feedback',
    to: '/Feedback',
    iconPath: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  },
];

/** Map a URL pathname to the rail item id that should be highlighted. The
 *  "longest matching `to` prefix wins" rule means deep routes (e.g.
 *  /Projects/Templates/abc) still highlight their parent rail item, and
 *  /Operator/Internal beats /Operator/Teams when the path starts with both. */
export function pickActiveId(pathname: string, isAdmin: boolean): string | undefined {
  const items = isAdmin
    ? OPERATOR_GROUPS.flatMap((g) => g.items)
    : [...CRM_PRIMARY, ...CRM_FOOTER];
  let bestId: string | undefined;
  let bestLen = 0;
  for (const it of items) {
    if (!it.to) continue;
    if (pathname === it.to || pathname.startsWith(it.to + '/')) {
      if (it.to.length > bestLen) {
        bestLen = it.to.length;
        bestId = it.id;
      }
    }
  }
  return bestId;
}
